import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { PortalTunnel } from "../components/PortalTunnel";
import { SilkField } from "../components/SilkField";
import { browserTimeZone } from "../lib/date";

const bookingQuery = `?tz=${encodeURIComponent(browserTimeZone)}`;
const everything = "EVERYTHING";
const glyphs = "∆ΨЖØΞ‡#%&?";
const initialEverythingGlyphs = Array.from(everything, (letter, index) => glyphs[(index * 5) % glyphs.length] ?? letter);

const profiles = [
  {
    index: "01",
    first: "SHAHIL",
    last: "MATHAPERSHAD",
    field: "Capital & Credit",
    booking: `/book/shahil-30${bookingQuery}`,
    core: "CAPITAL INTELLIGENCE",
    inputs: ["YOUR PROBLEMS", "WASTED MONEY", "RISK IN THE DARK"],
    outputs: ["VISIBLE RISK", "DEFENSIBLE DECISIONS", "CAPITAL THAT MOVES"],
  },
  {
    index: "02",
    first: "RASHIK",
    last: "SINGH",
    field: "Operations & Supply",
    booking: `/book/rashik-30${bookingQuery}`,
    core: "OPERATING INTELLIGENCE",
    inputs: ["YOUR BOTTLENECKS", "WASTED HOURS", "BROKEN FLOW"],
    outputs: ["MEASURABLE FLOW", "LESS WASTE", "OPERATIONS THAT HOLD"],
  },
] as const;

const inputPaths = [
  "M80 126 C292 126 302 252 490 252",
  "M80 310 C292 310 352 310 490 310",
  "M80 494 C292 494 302 368 490 368",
] as const;

const outputPaths = [
  "M710 252 C898 252 908 126 1120 126",
  "M710 310 C848 310 928 310 1120 310",
  "M710 368 C898 368 908 494 1120 494",
] as const;

const portalStatements = [
  { at: 0.12, text: "THE PROBLEM BENDS", side: "left" },
  { at: 0.36, text: "THE PATTERN APPEARS", side: "right" },
  { at: 0.61, text: "THE SYSTEM HOLDS", side: "left" },
  { at: 0.84, text: "THE FUTURE MOVES", side: "right" },
] as const;

const gravityInputs = [
  { text: "WASTED MONEY", x: "-37vw", y: "-28vh", turn: "-26deg" },
  { text: "WASTED HOURS", x: "-42vw", y: "-7vh", turn: "14deg" },
  { text: "HIDDEN RISK", x: "-34vw", y: "17vh", turn: "-12deg" },
  { text: "BROKEN FLOW", x: "-21vw", y: "34vh", turn: "22deg" },
  { text: "YOUR PROBLEMS", x: "-18vw", y: "-39vh", turn: "8deg" },
] as const;

const gravityOutputs = [
  { text: "CLARITY", x: "37vw", y: "-30vh", turn: "18deg" },
  { text: "CONTROL", x: "42vw", y: "-8vh", turn: "-12deg" },
  { text: "LEVERAGE", x: "35vw", y: "18vh", turn: "11deg" },
  { text: "MOTION", x: "22vw", y: "35vh", turn: "-18deg" },
  { text: "GROWTH", x: "18vw", y: "-39vh", turn: "-7deg" },
] as const;

const travelKeyframes = [
  [0.12, 0],
  [0.18, -100],
  [0.24, -170],
  [0.305, -250],
  [0.37, -360],
  [0.435, -470],
  [0.5, -570],
  [0.565, -670],
  [0.63, -770],
  [0.695, -870],
  [0.76, -970],
] as const;

const travelTangents = travelKeyframes.map((point, index) => {
  if (index === 0 || index === travelKeyframes.length - 1) return 0;
  const previous = travelKeyframes[index - 1];
  const next = travelKeyframes[index + 1];
  if (!previous || !next) return 0;
  return (next[1] - previous[1]) / (next[0] - previous[0]);
});

function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function smooth(value: number) {
  const t = clamp(value);
  return t * t * (3 - 2 * t);
}

function filmPosition(progress: number) {
  if (progress <= travelKeyframes[0][0]) return 0;
  for (let index = 0; index < travelKeyframes.length - 1; index += 1) {
    const from = travelKeyframes[index];
    const to = travelKeyframes[index + 1];
    if (!from || !to || progress > to[0]) continue;
    const local = clamp((progress - from[0]) / (to[0] - from[0]));
    const local2 = local * local;
    const local3 = local2 * local;
    const distance = to[0] - from[0];
    const fromTangent = travelTangents[index] ?? 0;
    const toTangent = travelTangents[index + 1] ?? 0;
    return (2 * local3 - 3 * local2 + 1) * from[1]
      + (local3 - 2 * local2 + local) * distance * fromTangent
      + (-2 * local3 + 3 * local2) * to[1]
      + (local3 - local2) * distance * toTangent;
  }
  if (progress <= 0.91) return -970;
  return -970 - smooth((progress - 0.91) / 0.09) * 100;
}

function SynapseMap({ profile, index }: { profile: (typeof profiles)[number]; index: number }) {
  const revealVariable = index === 0 ? "var(--name-one)" : "var(--name-two)";

  return (
    <div
      className={`synapse-map synapse-map--${index + 1}`}
      style={{ "--synapse": revealVariable } as CSSProperties}
      role="img"
      aria-label={`${profile.inputs.join(", ")} flow through RSM ${profile.core.toLowerCase()} into ${profile.outputs.join(", ")}`}
    >
      <svg className="synapse-map__paths" viewBox="0 0 1200 620" preserveAspectRatio="none" aria-hidden="true">
        <g className="synapse-map__lines synapse-map__lines--input">
          {inputPaths.map((path) => <path d={path} pathLength="1" key={path} />)}
        </g>
        <g className="synapse-map__lines synapse-map__lines--output">
          {outputPaths.map((path) => <path d={path} pathLength="1" key={path} />)}
        </g>
        <g className="synapse-map__packets synapse-map__packets--input">
          {inputPaths.map((path) => <path d={path} pathLength="1" key={`packet-${path}`} />)}
        </g>
        <g className="synapse-map__packets synapse-map__packets--output">
          {outputPaths.map((path) => <path d={path} pathLength="1" key={`packet-${path}`} />)}
        </g>
      </svg>

      <div className="synapse-map__nodes synapse-map__nodes--input" aria-hidden="true">
        {profile.inputs.map((label, nodeIndex) => (
          <span key={label} style={{ "--node-delay": nodeIndex * 0.08 } as CSSProperties}>{label}</span>
        ))}
      </div>

      <div className="synapse-map__field" aria-hidden="true"><i /><i /><i /><i /></div>

      <div className="synapse-map__core" aria-hidden="true">
        <i className="synapse-map__scan" />
        <b>RSM</b>
        <span>{profile.core}</span>
      </div>

      <div className="synapse-map__nodes synapse-map__nodes--output" aria-hidden="true">
        {profile.outputs.map((label, nodeIndex) => (
          <span key={label} style={{ "--node-delay": 0.5 + nodeIndex * 0.08 } as CSSProperties}>{label}</span>
        ))}
      </div>
    </div>
  );
}

export function HomePage() {
  const rootRef = useRef<HTMLElement>(null);
  const journeyRef = useRef<HTMLElement>(null);
  const bookingDialog = useRef<HTMLDialogElement>(null);
  const portalProgress = useRef(0);
  const portalVelocity = useRef(0);
  const targetProgress = useRef(0);
  const displayProgress = useRef(0);
  const loaderStarted = useRef(0);
  const letterRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [fieldReady, setFieldReady] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("odyssey-loading", !loaded);
    return () => document.body.classList.remove("odyssey-loading");
  }, [loaded]);

  useEffect(() => {
    if (loaded) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (loaderStarted.current === 0) loaderStarted.current = performance.now();
    const elapsed = performance.now() - loaderStarted.current;
    const total = reduced ? 50 : fieldReady ? 4_000 : 4_800;
    const delay = Math.max(0, total - elapsed);
    const timer = window.setTimeout(() => setLoaded(true), delay);
    return () => window.clearTimeout(timer);
  }, [fieldReady, loaded]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const film = root.querySelector<HTMLElement>(".odyssey-film");
    const buildPanel = root.querySelector<HTMLElement>(".film-panel--build");
    const [builderOne, builderTwo] = Array.from(root.querySelectorAll<HTMLElement>(".film-builder"));
    const interferencePanel = root.querySelector<HTMLElement>(".film-panel--interference");
    const cartographyPanel = root.querySelector<HTMLElement>(".film-panel--cartography");
    const apparatusPanel = root.querySelector<HTMLElement>(".film-panel--apparatus");
    const gravityPanel = root.querySelector<HTMLElement>(".film-panel--gravity");
    const assemblagePanel = root.querySelector<HTMLElement>(".film-panel--assemblage");
    const portalPanel = root.querySelector<HTMLElement>(".film-panel--portal");
    const signal = root.querySelector<SVGElement>(".film-signal");
    const portalMouth = root.querySelector<HTMLElement>(".film-portal-mouth");
    const velocityField = root.querySelector<HTMLElement>(".film-velocity");
    if (!film || !buildPanel || !builderOne || !builderTwo || !interferencePanel || !cartographyPanel || !apparatusPanel || !gravityPanel || !assemblagePanel || !portalPanel || !signal || !portalMouth || !velocityField) return;
    let frame = 0;
    let previous = 0;
    let kineticEnergy = 0;
    let kineticDirection = 1;
    let journeyTop = 0;
    let journeyDistance = 1;
    let lastGlitchFrame = -1;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const applyProgress = (journey: number) => {
      const hero = smooth(journey / 0.12);
      const nameOne = smooth((journey - 0.245) / 0.06);
      const nameTwo = smooth((journey - 0.31) / 0.06);
      const tube = smooth((journey - 0.135) / 0.625);
      const sceneOne = smooth((journey - 0.37) / 0.065);
      const sceneTwo = smooth((journey - 0.435) / 0.065);
      const sceneThree = smooth((journey - 0.5) / 0.065);
      const sceneFour = smooth((journey - 0.565) / 0.065);
      const sceneFive = smooth((journey - 0.63) / 0.065);
      const portal = clamp((journey - 0.76) / 0.15);
      portalProgress.current = portal;

      film.style.setProperty("--film-x", `${filmPosition(journey).toFixed(4)}vw`);
      buildPanel.style.setProperty("--hero", hero.toFixed(4));
      buildPanel.style.setProperty("--everything", smooth((hero - 0.18) / 0.7).toFixed(4));
      builderOne.style.setProperty("--name-one", nameOne.toFixed(4));
      builderTwo.style.setProperty("--name-two", nameTwo.toFixed(4));
      signal.style.setProperty("--tube", tube.toFixed(4));
      portalMouth.style.setProperty("--tube", tube.toFixed(4));
      interferencePanel.style.setProperty("--scene-one", sceneOne.toFixed(4));
      cartographyPanel.style.setProperty("--scene-two", sceneTwo.toFixed(4));
      apparatusPanel.style.setProperty("--scene-three", sceneThree.toFixed(4));
      gravityPanel.style.setProperty("--scene-four", sceneFour.toFixed(4));
      assemblagePanel.style.setProperty("--scene-five", sceneFive.toFixed(4));
      portalPanel.style.setProperty("--portal", portal.toFixed(4));
      portalMouth.style.setProperty("--portal", portal.toFixed(4));
      portalMouth.style.setProperty("--portal-angle", `${(portal * 720).toFixed(2)}deg`);

      portalStatements.forEach((statement, index) => {
        const distance = Math.abs(portal - statement.at);
        const reveal = 1 - smooth(clamp((distance - 0.045) / 0.105));
        portalPanel.style.setProperty(`--copy-${index}`, reveal.toFixed(4));
      });

      const glitch = clamp((hero - 0.2) / 0.66);
      const glitchFrame = Math.floor(glitch * 46);
      if (glitchFrame !== lastGlitchFrame) {
        lastGlitchFrame = glitchFrame;
        Array.from(everything).forEach((letter, index) => {
          const settleAt = 0.16 + index * 0.082;
          const nextLetter = glitch >= settleAt || reduced
            ? letter
            : glyphs[(glitchFrame * 3 + index * 5) % glyphs.length] ?? letter;
          const node = letterRefs.current[index];
          if (node && node.textContent !== nextLetter) node.textContent = nextLetter;
        });
      }
    };

    const render = (now: number) => {
      frame = 0;
      const delta = previous === 0 ? 1 / 60 : clamp((now - previous) / 1000, 0, 0.05);
      previous = now;
      const progressBeforeFrame = displayProgress.current;
      const remaining = targetProgress.current - displayProgress.current;
      const followRate = Math.abs(remaining) > 0.055 ? 30 : 22;
      const damping = reduced ? 1 : 1 - Math.exp(-delta * followRate);
      displayProgress.current += remaining * damping;
      if (Math.abs(targetProgress.current - displayProgress.current) < 0.000035) {
        displayProgress.current = targetProgress.current;
      }

      const signedVelocity = (displayProgress.current - progressBeforeFrame) / Math.max(delta, 0.001);
      if (Math.abs(signedVelocity) > 0.002) kineticDirection = Math.sign(signedVelocity);
      const kineticTarget = reduced ? 0 : clamp(Math.abs(signedVelocity) * 0.38);
      const kineticRate = kineticTarget > kineticEnergy ? 24 : 6.5;
      kineticEnergy += (kineticTarget - kineticEnergy) * (1 - Math.exp(-delta * kineticRate));
      if (kineticEnergy < 0.0005) kineticEnergy = 0;
      portalVelocity.current = kineticDirection * kineticEnergy;
      velocityField.style.setProperty("--kinetic", kineticEnergy.toFixed(4));
      velocityField.style.setProperty("--smear-x", `${(kineticDirection * kineticEnergy * 82).toFixed(2)}px`);
      velocityField.style.setProperty("--smear-back", `${(-kineticDirection * kineticEnergy * 48).toFixed(2)}px`);
      velocityField.style.setProperty("--smear-tilt", `${(kineticDirection * kineticEnergy * 7).toFixed(2)}deg`);

      applyProgress(displayProgress.current);
      if (displayProgress.current !== targetProgress.current || kineticEnergy > 0.004) {
        frame = window.requestAnimationFrame(render);
      } else {
        portalVelocity.current = 0;
        root.classList.remove("is-scrolling");
      }
    };

    const queueRender = () => {
      root.classList.add("is-scrolling");
      if (frame) return;
      previous = 0;
      frame = window.requestAnimationFrame(render);
    };

    const measureTarget = () => {
      targetProgress.current = clamp((window.scrollY - journeyTop) / journeyDistance);
      if (reduced) {
        displayProgress.current = targetProgress.current;
        portalVelocity.current = 0;
        velocityField.style.setProperty("--kinetic", "0");
        applyProgress(displayProgress.current);
        return;
      }
      queueRender();
    };

    const measureBounds = () => {
      const journey = journeyRef.current;
      if (!journey) return;
      journeyTop = journey.getBoundingClientRect().top + window.scrollY;
      journeyDistance = Math.max(journey.offsetHeight - window.innerHeight, 1);
      measureTarget();
    };

    measureBounds();
    window.addEventListener("scroll", measureTarget, { passive: true });
    window.addEventListener("resize", measureBounds, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", measureTarget);
      window.removeEventListener("resize", measureBounds);
      root.classList.remove("is-scrolling");
    };
  }, []);

  return (
    <main className="odyssey odyssey--film" id="top" ref={rootRef} aria-busy={!loaded}>
      <div
        className={`odyssey-loader${loaded ? " is-complete" : ""}`}
        role="status"
        aria-label={loaded ? undefined : "Preparing the RSM experience"}
        aria-hidden={loaded}
      >
        <div className="odyssey-loader__panel odyssey-loader__panel--top" />
        <div className="odyssey-loader__panel odyssey-loader__panel--bottom" />
        <p>ENTER THE FIELD</p>
      </div>

      <section className="odyssey-journey" ref={journeyRef} aria-label="RSM Consulting story">
        <div className="odyssey-stage">
          <header className="odyssey-nav">
            <a href="#top" aria-label="RSM Consulting home"><strong>RSM</strong><span>Consulting</span></a>
            <button type="button" onClick={() => bookingDialog.current?.showModal()}>Work with us ↗</button>
          </header>

          <div className="film-velocity" aria-hidden="true"><i /><i /><i /><i /></div>

          <div className="odyssey-film">
            <section className="film-panel film-panel--build" aria-labelledby="build-title">
              <SilkField active onReady={() => setFieldReady(true)} />
              <div className="build-act__veil" />
              <h1 id="build-title" className="build-title">
                <span className="build-title__we" aria-label="We build">
                  <span className="build-title__outline">WE BUILD</span>
                  <span className="build-title__fill">WE BUILD</span>
                </span>
                <span className="build-title__everything" data-word="EVERYTHING" aria-label="Everything">
                  {initialEverythingGlyphs.map((letter, index) => (
                    <span ref={(node) => { letterRefs.current[index] = node; }} key={index} style={{ "--letter": index } as CSSProperties}>{letter}</span>
                  ))}
                </span>
              </h1>
            </section>

            <section className="film-panel film-panel--transfer" aria-hidden="true"><strong>EVERYTHING</strong><span>passes through two minds</span></section>

            <section className="film-panel film-panel--intro" aria-label="The builders">
              <h2>TWO MINDS.<br />ONE CONTINUOUS LINE.</h2>
              <p>We enter where the answer is unclear, then build the system that makes it obvious.</p>
            </section>

            {profiles.map((profile, index) => (
              <article className={`film-builder film-builder--${index + 1}`} key={profile.index}>
                <header><span>{profile.field}</span></header>
                <div className="film-builder__name" aria-label={`${profile.first} ${profile.last}`}>
                  <div><span>{profile.first}</span><span>{profile.first}</span></div>
                  <div><span>{profile.last}</span><span>{profile.last}</span></div>
                </div>
                <SynapseMap profile={profile} index={index} />
                <footer><Link to={profile.booking}>Book with {profile.first.charAt(0) + profile.first.slice(1).toLowerCase()} ↗</Link></footer>
              </article>
            ))}

            <section className="film-panel film-panel--interference" aria-labelledby="interference-title">
              <div className="interference-eye" aria-hidden="true">
                <i /><i /><i /><i />
              </div>
              <h2 id="interference-title"><span>READ</span><span>THE NOISE</span></h2>
              <div className="interference-type" aria-hidden="true">NOISE&nbsp;NOISE&nbsp;NOISE</div>
            </section>

            <section className="film-panel film-panel--cartography" aria-labelledby="cartography-title">
              <svg className="cartography-art" viewBox="0 0 1600 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <defs>
                  <pattern id="cartography-check" width="72" height="72" patternUnits="userSpaceOnUse">
                    <rect width="36" height="36" fill="#111" />
                    <rect x="36" y="36" width="36" height="36" fill="#111" />
                  </pattern>
                </defs>
                <path className="cartography-art__blue" d="M-120 695 C182 374 292 936 604 602 C884 300 1050 700 1320 274 C1432 98 1550 138 1720 18 L1720 1080 L-120 1080 Z" />
                <path className="cartography-art__coral" d="M-100 202 C176 28 334 428 574 224 C820 14 1000 302 1214 144 C1418 -8 1530 70 1710 248 L1710 442 C1462 292 1370 222 1188 420 C982 644 772 336 542 566 C286 822 132 478 -100 660 Z" />
                <path className="cartography-art__check" d="M-92 366 C170 174 318 662 570 444 C830 218 1018 544 1234 344 C1438 156 1570 226 1710 390 L1710 566 C1460 386 1360 392 1168 604 C966 826 764 538 530 742 C290 952 98 636 -92 816 Z" />
                <path className="cartography-art__ink" d="M-90 746 C232 466 370 956 658 672 C932 402 1094 762 1372 472 C1500 338 1604 358 1704 428" pathLength="1" />
                <circle cx="1288" cy="235" r="112" fill="#dfff00" stroke="#111" strokeWidth="20" />
              </svg>
              <h2 id="cartography-title"><span>FIND</span><em>THE</em><span>PATTERN</span></h2>
            </section>

            <section className="film-panel film-panel--apparatus" aria-labelledby="apparatus-title">
              <div className="apparatus-grid" aria-hidden="true" />
              <div className="apparatus-object" aria-hidden="true">
                <i /><i /><i /><i /><i /><i />
                <b>RSM</b>
              </div>
              <h2 id="apparatus-title"><span>BUILD</span><span>WHAT</span><span>HOLDS</span></h2>
            </section>

            <section className="film-panel film-panel--gravity" aria-labelledby="gravity-title">
              <div className="gravity-field" aria-hidden="true">
                <div className="gravity-field__rings"><i /><i /><i /><i /><i /></div>
                <div className="gravity-field__vectors">
                  {Array.from({ length: 12 }, (_, index) => <i key={index} style={{ "--vector": index } as CSSProperties} />)}
                </div>
                <div className="gravity-field__words gravity-field__words--input">
                  {gravityInputs.map((item, index) => (
                    <span key={item.text} style={{ "--gravity-x": item.x, "--gravity-y": item.y, "--gravity-turn": item.turn, "--gravity-index": index } as CSSProperties}>{item.text}</span>
                  ))}
                </div>
                <div className="gravity-field__words gravity-field__words--output">
                  {gravityOutputs.map((item, index) => (
                    <span key={item.text} style={{ "--gravity-x": item.x, "--gravity-y": item.y, "--gravity-turn": item.turn, "--gravity-index": index } as CSSProperties}>{item.text}</span>
                  ))}
                </div>
                <div className="gravity-field__core"><span>PROBLEM</span><b>RSM</b><span>SYSTEM</span></div>
              </div>
              <p className="gravity-kicker">Transformation engine / 04</p>
              <h2 id="gravity-title"><span>BREAK</span><em>THE</em><span>LOOP</span></h2>
            </section>

            <section className="film-panel film-panel--assemblage" aria-labelledby="assemblage-title">
              <div className="assemblage-canvas" aria-hidden="true">
                <div className="assemblage-piece assemblage-piece--moire"><i /><i /></div>
                <div className="assemblage-piece assemblage-piece--wire"><i /><i /><i /></div>
                <div className="assemblage-piece assemblage-piece--card">
                  <small>RSM / CONSULTING</small><b>WE BUILD<br />WHAT MOVES</b><span>SHAHIL × RASHIK</span>
                </div>
                <svg className="assemblage-piece assemblage-piece--ribbon" viewBox="0 0 420 220">
                  <path d="M-20 154 C56 18 122 246 196 88 C254 -34 324 184 446 44" pathLength="1" />
                </svg>
                <div className="assemblage-piece assemblage-piece--barcode"><i /></div>
                <div className="assemblage-piece assemblage-piece--eye"><i /><b>01</b></div>
                <div className="assemblage-piece assemblage-piece--ladder"><i /><i /><i /><i /><i /></div>
                <div className="assemblage-piece assemblage-piece--burst">✳</div>
                <div className="assemblage-piece assemblage-piece--stamp">SYSTEM<br />ONLINE</div>
                <div className="assemblage-piece assemblage-piece--coordinates">26.2041° S<br />28.0473° E<br />RSM—ZA</div>
                <div className="assemblage-piece assemblage-piece--column">STRATEGY&nbsp; / &nbsp;CAPITAL&nbsp; / &nbsp;OPERATIONS&nbsp; / &nbsp;SYSTEMS</div>
              </div>
              <p className="assemblage-kicker">From signal to substance / 05</p>
              <h2 id="assemblage-title"><span data-echo="MAKE">MAKE</span><em>IT</em><span data-echo="REAL">REAL</span></h2>
            </section>

            <section className="film-panel film-panel--portal" aria-labelledby="portal-title">
              <PortalTunnel progressRef={portalProgress} velocityRef={portalVelocity} />
              <h2 className="visually-hidden" id="portal-title">Inside the RSM process</h2>
              <div className="portal-copy">
                {portalStatements.map((statement, index) => (
                  <p className={`portal-copy__line portal-copy__line--${statement.side}`} key={statement.text} style={{ "--copy-index": index } as CSSProperties}>{statement.text}</p>
                ))}
              </div>
            </section>

            <section className="film-panel film-panel--exit" id="contact" aria-labelledby="contact-title">
              <h2 id="contact-title">LET&apos;S WORK<br />TOGETHER</h2>
              <button type="button" onClick={() => bookingDialog.current?.showModal()}>Start here <span>↗</span></button>
            </section>

            <svg className="film-signal" viewBox="0 0 8700 220" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="film-spectrum" x1="0" x2="1"><stop offset="0" stopColor="#dfff00" /><stop offset=".3" stopColor="#00eaff" /><stop offset=".58" stopColor="#2347ff" /><stop offset=".8" stopColor="#8b00ff" /><stop offset="1" stopColor="#ff158c" /></linearGradient>
              </defs>
              <path className="film-signal__glow" d="M-100 112 C160 18 320 198 620 104 S1100 22 1390 116 S1860 202 2170 102 S2720 24 3040 116 S3440 184 3800 108 S4320 18 4680 120 S5160 208 5510 96 S6060 12 6800 110 S7340 202 7720 94 S8280 22 8800 110" pathLength="1" />
              <path className="film-signal__core" d="M-100 112 C160 18 320 198 620 104 S1100 22 1390 116 S1860 202 2170 102 S2720 24 3040 116 S3440 184 3800 108 S4320 18 4680 120 S5160 208 5510 96 S6060 12 6800 110 S7340 202 7720 94 S8280 22 8800 110" pathLength="1" />
            </svg>
            <div className="film-portal-mouth" aria-hidden="true"><i /><i /><i /></div>
          </div>
        </div>
      </section>

      <dialog className="odyssey-dialog" ref={bookingDialog}>
        <header><p>Choose your specialist</p><button type="button" onClick={() => bookingDialog.current?.close()}>Close ×</button></header>
        <div>{profiles.map((profile) => <Link key={profile.index} to={profile.booking}><span>{profile.index} / {profile.field}</span><strong>{profile.first} {profile.last}</strong><span>Book 30 minutes ↗</span></Link>)}</div>
      </dialog>
    </main>
  );
}
